import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import styledContainerQuery from 'styled-container-query';
import { useObserver } from 'mobx-react-lite';

import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import { useModals, ModalNames } from 'src/components/modals';
import { Typography } from 'src/typography';
import { AppleLogin, GoogleLogin } from 'src/components/social-login';
import { tracker } from 'src/utils/analytics';

type TrackEventProps = {
  signIn: boolean;
  type: 'email' | 'sso';
};

export const LoginV1 = (): JSX.Element | null => {
  const { openModal } = useModals();
  const User = useUser();
  const { isKiosk } = useUI();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const isLoading = useObserver(() => User.loading);

  // window is unavailable during SSR, so seed empty and resolve the redirect target after mount.
  // This keeps the server render and first client render identical (clean hydration); the real
  // href is populated well before the user can trigger a social login. Mirrors LoginV2.
  const [redirectUrl, setRedirectUrl] = useState('');
  useEffect(() => {
    setRedirectUrl(window.location.href);
  }, []);

  if (isKiosk || (isLoggedIn && !isLoading)) {
    return null;
  }

  const handleLogin = (): void => {
    void openModal(ModalNames.login);
    trackEvent({ signIn: true, type: 'email' });
  };

  const trackEvent = ({ signIn, type }: TrackEventProps): void => {
    tracker.gaGTMClickEvent({
      eventCategory: 'account',
      eventAction: signIn ? 'signed in' : 'created account',
      eventLabel: `${type}: {pageType}`,
    });
  };

  return (
    <Wrapper>
      <Typography.Heading size='xsmall' tag='h3'>
        Login for easy checkout and re-ordering
      </Typography.Heading>

      <Buttons>
        <ButtonWrapper>
          <GoogleLogin redirectUrl={redirectUrl} onSubmit={() => trackEvent({ signIn: true, type: 'sso' })} />
        </ButtonWrapper>

        <ButtonWrapper>
          <AppleLogin redirectUrl={redirectUrl} onSubmit={() => trackEvent({ signIn: true, type: 'sso' })} />
        </ButtonWrapper>
      </Buttons>

      <Byline size='medium' weight='semi'>
        <InlineLink onClick={handleLogin}>Log in or sign up with email</InlineLink>
      </Byline>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  background: #f5f6f7;
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  padding: 24px;
`;

const Byline = styled(Typography.Label)`
  color: #646d72;
  margin-top: 8px;
`;

// Using this as the container query instead of the Wrapper element just for ease of adapting styles to account for horizontal padding in the query
const Buttons = styledContainerQuery.div`
  align-items: center;
  display: flex;
  margin-top: 24px;
  gap: 20px;

  &:container(max-width: 450px) {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }
`;

const ButtonWrapper = styled.div`
  flex: 1;
`;

const InlineLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;
